// The face rig of MedSnapp's cast, ported from MedSnapp's website repo (src/data/beat-rig.js: EYES, LID_TRAVEL,
// LOW_TRAVEL, MOUTHS and the poses used here, values as given). The drawings in web/public/cast are that rig rendered in
// its neutral pose with every style inlined; poseCast() finds the rig's parts in such a drawing (anchored on the mouth,
// the one element with an id of its own) and sets a pose with CSS transforms and opacity only. The mouth is swapped,
// never morphed. If a drawing does not have the rig's shape, nothing is changed and the neutral face stays.
import type { CastMood } from './CastMascot';

export const EYES = { l: [78, 92], r: [122, 92], rx: 15.5, ry: 19 } as const;
export const LID_TRAVEL = 38;
export const LOW_TRAVEL = 24;

export const MOUTHS = {
  smile: 'M86 120 Q100 134 114 120 Q100 127 86 120 Z',
  open: 'M85 118 Q100 142 115 118 Q100 122 85 118 Z',
  laugh: 'M79 113 Q100 156 121 113 Q100 121 79 113 Z',
  o: 'M100 115 C108 115 111 122 111 128 C111 135 106 140 100 140 C94 140 89 135 89 128 C89 122 92 115 100 115 Z',
  flat: 'M89 123 Q100 125 111 122 Q100 129 89 123 Z',
  frown: 'M86 131 Q100 115 114 131 Q100 124 86 131 Z',
  smirk: 'M88 124 Q104 133 118 114 Q111 136 88 124 Z',
  tiny: 'M96 126 C96 122 104 122 104 126 C104 130 96 130 96 126 Z',
} as const;
type Mouth = keyof typeof MOUTHS;

interface Lid { c: number; r: number }
interface Brow { o: number; y: number; r: number }
export interface Pose {
  lidL: Lid; lidR: Lid; low: number;
  pupil: { x: number; y: number; s: number; hide?: boolean };
  browL: Brow; browR: Brow;
  mouth: Mouth; tongue: boolean; arms: [number, number];
  body: { sx: number; sy: number; r: number };
  x: string[];
}
const P = (o: Partial<Pose>): Pose => ({
  lidL: { c: 0.04, r: 0 }, lidR: { c: 0.04, r: 0 }, low: 0,
  pupil: { x: 1, y: 2, s: 1, hide: false },
  browL: { o: 0, y: 0, r: 0 }, browR: { o: 0, y: 0, r: 0 },
  mouth: 'smile', tongue: false, arms: [18, -18], body: { sx: 1, sy: 1, r: 0 }, x: [],
  ...o,
});
const SHUT: Lid = { c: 1, r: 0 };

/** The website rig's poses for the six moods (beat-rig.js POSES, values as given). */
export const POSES = {
  neutral: P({}),
  joy: P({ lidL: SHUT, lidR: SHUT, mouth: 'laugh', tongue: true, arms: [168, -168], body: { sx: 0.96, sy: 1.05, r: 0 }, x: ['arcs', 'pop'] }),
  proud: P({ lidL: { c: 0.38, r: -4 }, lidR: { c: 0.38, r: 4 }, low: 0.22, pupil: { x: 0, y: 1, s: 1 }, mouth: 'smirk', browL: { o: 1, y: -2, r: -8 }, browR: { o: 1, y: -2, r: 8 }, arms: [-28, 28], body: { sx: 0.98, sy: 1.04, r: 0 }, x: ['pop'] }),
  surprised: P({ lidL: { c: 0, r: 0 }, lidR: { c: 0, r: 0 }, pupil: { x: 0, y: 0, s: 0.62 }, browL: { o: 1, y: -11, r: 0 }, browR: { o: 1, y: -11, r: 0 }, mouth: 'o', arms: [62, -62], body: { sx: 0.95, sy: 1.07, r: 0 } }),
  sad: P({ lidL: { c: 0.36, r: -15 }, lidR: { c: 0.36, r: 15 }, pupil: { x: 0, y: 5, s: 1 }, browL: { o: 1, y: -2, r: -15 }, browR: { o: 1, y: -2, r: 15 }, mouth: 'frown', arms: [4, -4], body: { sx: 1.03, sy: 0.95, r: 0 } }),
  sleepy: P({ lidL: SHUT, lidR: SHUT, mouth: 'tiny', arms: [4, -4], body: { sx: 1, sy: 0.98, r: 5 }, x: ['shut', 'zz'] }),
} satisfies Record<string, Pose>;
export type PoseName = keyof typeof POSES;

/** Each mood's pose: right joy, wrong sad, partial surprised, none sleepy, podium proud, idle neutral. */
export const MOOD_POSE: Readonly<Record<CastMood, PoseName>> = { right: 'joy', wrong: 'sad', partial: 'surprised', none: 'sleepy', podium: 'proud', idle: 'neutral' };

// The face group's children, in the rig's order (BeatRig.astro), around the mouth.
const EXTRAS_BEFORE = ['lash', 'shut', 'arcs'] as const;          // the three just before the mouth
const EXTRAS_AFTER = ['tongue', 'pop', 'hearts', 'blush', 'dots', 'question', 'sweat', 'tear', 'vein', 'zz'] as const;

const px = (n: number) => `${n}px`;
const about = (cx: number, cy: number, inner: string) => `translate(${px(cx)}, ${px(cy)}) ${inner} translate(${px(-cx)}, ${px(-cy)})`;
const rotateOf = (el: Element | null): [number, number] | undefined => {
  const m = el?.getAttribute('transform')?.match(/rotate\(\s*-?[\d.]+\s+(-?[\d.]+)\s+(-?[\d.]+)\s*\)/);
  return m ? [Number(m[1]), Number(m[2])] : undefined;
};
const set = (el: Element | null | undefined, transform?: string, opacity?: number) => {
  if (!(el instanceof SVGElement)) return;
  if (transform !== undefined) el.style.transform = transform;
  if (opacity !== undefined) el.style.opacity = String(opacity);
};

/** The rig's parts in a drawing, or undefined if the drawing does not have the rig's shape. */
function partsOf(svg: SVGSVGElement) {
  const mouth = svg.querySelector('path[id$="-mouth"]');
  const face = mouth?.parentElement;
  if (!mouth || !face) return undefined;
  const kids = [...face.children];
  const m = kids.indexOf(mouth);
  if (m !== 7 || kids.length !== m + 1 + EXTRAS_AFTER.length) return undefined;
  const seat = face.parentElement; const root = seat?.parentElement;
  if (!root) return undefined;
  const arms = [root.children[0], root.children[1]];
  const shoulders = arms.map(rotateOf);
  if (!shoulders[0] || !shoulders[1]) return undefined;
  const eye = (g: Element) => {
    const clip = g.children[0];
    const look = clip?.children[1]; const lid = clip?.children[3]; const low = clip?.children[4];
    return { look, pupil: look?.children[0], lid, low };
  };
  const extras: Record<string, Element> = {};
  EXTRAS_BEFORE.forEach((n, i) => { extras[n] = kids[m - 3 + i]; });
  EXTRAS_AFTER.forEach((n, i) => { extras[n] = kids[m + 1 + i]; });
  return { mouth, root, arms, shoulders: shoulders as [number, number][], browL: kids[0], browR: kids[1], eyeL: eye(kids[2]), eyeR: eye(kids[3]), extras };
}

/** Sets a pose on a cast drawing that is in the page. Returns false (and changes nothing) if the drawing is not the rig's. */
export function poseCast(svg: SVGSVGElement, name: PoseName): boolean {
  const parts = partsOf(svg);
  if (!parts) return false;
  const p = POSES[name];
  set(parts.root, `translate(${px(100)}, ${px(180)}) rotate(${p.body.r}deg) scale(${p.body.sx}, ${p.body.sy}) translate(${px(-100)}, ${px(-180)})`);
  parts.arms.forEach((a, i) => { const [sx, sy] = parts.shoulders[i]; set(a, about(sx, sy, `rotate(${p.arms[i]}deg)`)); });
  for (const [side, brow, at] of [['L', p.browL, EYES.l], ['R', p.browR, EYES.r]] as const) {
    set(side === 'L' ? parts.browL : parts.browR, `translate(0px, ${px(brow.y)}) ${about(at[0], 66, `rotate(${brow.r}deg)`)}`, brow.o);
  }
  for (const [eye, lid, at] of [[parts.eyeL, p.lidL, EYES.l], [parts.eyeR, p.lidR, EYES.r]] as const) {
    const [cx, cy] = at;
    set(eye.look, undefined, p.pupil.hide ? 0 : 1);
    set(eye.pupil, `translate(${px(p.pupil.x)}, ${px(p.pupil.y)}) ${about(cx, cy, `scale(${p.pupil.s})`)}`);
    set(eye.lid, `translate(0px, ${px(lid.c * LID_TRAVEL)}) ${about(cx, cy, `rotate(${lid.r}deg)`)}`);
    set(eye.low, `translate(0px, ${px(-p.low * LOW_TRAVEL)})`);
  }
  parts.mouth.setAttribute('d', MOUTHS[p.mouth]);
  for (const [n, el] of Object.entries(parts.extras)) {
    if (n === 'tongue') set(el.firstElementChild, undefined, p.tongue ? 1 : 0);
    else set(el, undefined, p.x.includes(n) ? 1 : 0);
  }
  return true;
}

/** A drawing's text with its ids made unique, so two drawings on one page never share a clip path. */
export function uniqueIds(svgText: string, uid: string): string {
  const prefix = svgText.match(/id="([A-Za-z0-9_]+)-b"/)?.[1];
  return prefix ? svgText.split(prefix).join(uid) : svgText;
}
